import Phaser from 'phaser';

/**
 * ParticleSystem: Creates and manages particle effects
 * for explosions, shield damage, thruster glow, and UFO trails.
 * All effects use neon glow aesthetic with additive blending.
 */
export class ParticleSystem {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Create a neon explosion effect at the given position.
   * Alien explosions: colored particles matching the alien type.
   * Uses additive blending for neon glow feel with varied particle sizes.
   */
  createExplosion(x: number, y: number, color: number, count: number = 20): void {
    // Create a bright flash at the center
    const flash = this.scene.add.circle(x, y, 12, 0xffffff);
    flash.setAlpha(0.8);
    flash.setBlendMode(Phaser.BlendModes.ADD);
    flash.setDepth(50);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 2.5,
      duration: 150,
      onComplete: () => flash.destroy(),
    });

    // Create dim color for outer glow particles
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const dimColor = ((r >> 1) << 16) | ((g >> 1) << 8) | (b >> 1);

    for (let i = 0; i < count; i++) {
      // Randomize size - some small fast particles, some larger slower ones
      const isLarge = Math.random() < 0.3;
      const radius = isLarge ? Phaser.Math.Between(3, 5) : Phaser.Math.Between(1, 3);
      const speed = isLarge ? 0.6 : 1.0;

      // Use a radial spread with random angle
      const angle = Math.random() * Math.PI * 2;
      const distance = Phaser.Math.Between(20, 60) * speed;
      const targetX = x + Math.cos(angle) * distance;
      const targetY = y + Math.sin(angle) * distance;

      // Create glow behind particle (additive blend)
      if (isLarge) {
        const glow = this.scene.add.circle(x, y, radius * 2.5, dimColor);
        glow.setAlpha(0.4);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        glow.setDepth(49);
        this.scene.tweens.add({
          targets: glow,
          x: targetX,
          y: targetY,
          alpha: 0,
          scale: 0.3,
          duration: Phaser.Math.Between(300, 600) / speed,
          onComplete: () => glow.destroy(),
        });
      }

      // Main bright particle
      const particle = this.scene.add.circle(x, y, radius, color);
      particle.setAlpha(1);
      particle.setBlendMode(Phaser.BlendModes.ADD);
      particle.setDepth(50);

      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: isLarge ? 0.2 : 0,
        duration: Phaser.Math.Between(200, 500) / speed,
        ease: 'Quad.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  }

  /**
   * Create a player death explosion - distinctly different from alien explosions.
   * Uses cyan particles with screen shake, more particles, and a ring burst effect.
   */
  createPlayerExplosion(x: number, y: number): void {
    // Large bright white flash
    const flash = this.scene.add.circle(x, y, 20, 0xffffff);
    flash.setAlpha(0.9);
    flash.setBlendMode(Phaser.BlendModes.ADD);
    flash.setDepth(50);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 3.5,
      duration: 250,
      onComplete: () => flash.destroy(),
    });

    // Expanding ring effect
    const ring = this.scene.add.circle(x, y, 5, 0x00ffff);
    ring.setAlpha(0.6);
    ring.setBlendMode(Phaser.BlendModes.ADD);
    ring.setDepth(49);
    ring.setFillStyle(0x000000, 0); // hollow
    ring.setStrokeStyle(2, 0x00ffff, 0.8);
    this.scene.tweens.add({
      targets: ring,
      scale: 6,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Cyan core particles (main explosion)
    const cyanColor = 0x00ffff;
    const dimCyan = 0x007777;
    for (let i = 0; i < 30; i++) {
      const isLarge = Math.random() < 0.35;
      const radius = isLarge ? Phaser.Math.Between(3, 6) : Phaser.Math.Between(1, 3);
      const angle = Math.random() * Math.PI * 2;
      const distance = Phaser.Math.Between(25, 80);
      const targetX = x + Math.cos(angle) * distance;
      const targetY = y + Math.sin(angle) * distance;

      // Glow behind larger particles
      if (isLarge) {
        const glow = this.scene.add.circle(x, y, radius * 2.5, dimCyan);
        glow.setAlpha(0.5);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        glow.setDepth(49);
        this.scene.tweens.add({
          targets: glow,
          x: targetX,
          y: targetY,
          alpha: 0,
          scale: 0.2,
          duration: Phaser.Math.Between(400, 700),
          onComplete: () => glow.destroy(),
        });
      }

      const particle = this.scene.add.circle(x, y, radius, cyanColor);
      particle.setAlpha(1);
      particle.setBlendMode(Phaser.BlendModes.ADD);
      particle.setDepth(50);

      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: isLarge ? 0.15 : 0,
        duration: Phaser.Math.Between(300, 700),
        ease: 'Quad.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    // Add some white hot sparks for extra drama
    for (let i = 0; i < 8; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Phaser.Math.Between(40, 100);
      const spark = this.scene.add.circle(x, y, 1, 0xffffff);
      spark.setAlpha(1);
      spark.setBlendMode(Phaser.BlendModes.ADD);
      spark.setDepth(51);

      this.scene.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        duration: Phaser.Math.Between(200, 500),
        ease: 'Cubic.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  }

  /**
   * Create shield damage sparks - small blue-green particles.
   */
  createShieldSparks(x: number, y: number): void {
    for (let i = 0; i < 8; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Phaser.Math.Between(8, 25);
      const radius = Phaser.Math.Between(1, 2);
      const particle = this.scene.add.circle(x, y, radius, 0x00cc88);
      particle.setAlpha(0.8);
      particle.setBlendMode(Phaser.BlendModes.ADD);
      particle.setDepth(50);

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0,
        duration: Phaser.Math.Between(150, 350),
        ease: 'Quad.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  }

  /**
   * Create screen shake effect.
   */
  screenShake(intensity: number = 0.01, duration: number = 200): void {
    this.scene.cameras.main.shake(duration, intensity);
  }
}
